<template>
    <div class="family-patents-list pa-flex pa-h-full pa-w-full pa-flex-col pa-overflow-hidden pa-text-gray-1100">
        <FamilyPatentsLoading v-if="loadingList" />
        <template v-else>
            <div
                v-if="isFamilySearchFromWorkspace"
                class="pa-px-4 pa-py-2"
            >
                <ElPopover
                    :visibleArrow="false"
                    :openDelay="200"
                    placement="right"
                    trigger="hover"
                >
                    <div
                        slot="reference"
                        class="pa-mt-1 pa-flex pa-h-4 pa-items-center"
                    >
                        <img
                            v-if="workspaceInfo.spaceS3Key"
                            class="pa-mr-1 pa-h-4 pa-w-4 pa-rounded"
                            :src="workspaceInfo.spaceS3Key"
                            alt=""
                        >
                        <img
                            v-else-if="workspaceInfo.spaceIcon"
                            :src="icons[workspaceInfo.spaceIcon]"
                            class="pa-mr-1 pa-h-4 pa-w-4 pa-rounded"
                            alt=""
                        >
                        <span class="pa-flex pa-overflow-hidden pa-text-xs pa-text-gray-90">
                            <span
                                class="pa-flex-1 pa-truncate"
                            >{{ workspaceInfo.spaceName }}</span>
                            <span
                                class="pa-shrink-0"
                            > > {{ workspaceInfo.folderName }}</span>
                        </span>
                    </div>
                    <p
                        style="max-width: 526px; word-break: break-word; text-align: left;"
                        class="pa-flex pa-overflow-hidden"
                    >
                        <span>{{ $t('customFields.currentFolderPath') }}</span>
                        <span
                            class="pa-flex-1 pa-truncate"
                        >{{ workspaceInfo.spaceName }}</span>
                        <span class="pa-shrink-0"> > </span>
                        <span
                            class="pa-shrink-0"
                        >{{ workspaceInfo.folderName }}</span>
                    </p>
                </ElPopover>
            </div>
            <div
                v-if="hasFamilyOverview"
                class="family-patents-list__overview pa-shrink-0 pa-cursor-pointer pa-p-2 pa-pl-4 pa-shadow-[0_4px_10px_0_rgba(0,0,0,.06)] hover:pa-bg-gray-20"
                :class="{
                    'pa-bg-gray-30': isInFamilyOverview,
                }"
                @click="goToFamilyOverview"
            >
                <p class="pa-leading-6 pa-text-green-600">
                    {{ t('patent-view.familySearch.overview') }}
                </p>
                <div
                    v-if="!isFamilySearchFromWorkspace"
                    class="pa-mt-1 pa-inline-flex pa-select-none pa-items-center"
                >
                    <input
                        type="checkbox"
                        :checked="skipNoHit"
                        class="th-checkbox"
                        @change="changePatentsListForHit"
                        @click.stop
                    >
                    <span class="pa-ml-1 pa-leading-5">{{ t('patent-view.familySearch.hitOption') }}</span>
                </div>
            </div>
            <div class="family-patents-list__scroll pa-flex-1 pa-overflow-y-auto pa-overflow-x-hidden">
                <div class="family-patents-list__main">
                    <div
                        v-for="(item, index) in patentsList"
                        :key="item.patent_id + index"
                        class="patents-list-block"
                        :data-patentId="item.patent_id"
                        :data-pn="item.PN"
                        @click="goToPatentDetail(item)"
                    >
                        <div
                            class="family-patents-list__main__item pa-group pa-relative pa-cursor-pointer pa-p-2 pa-pl-4 pa-text-xs hover:pa-bg-gray-20"
                            :class="{
                                'list-active pa-bg-gray-30': !isInFamilyOverview && patentId === item.patent_id
                            }"
                        >
                            <ElTooltip
                                v-if="item.hit"
                                :visibleArrow="false"
                                :content="t('familyPatent.tableContent.tips')"
                                effect="light"
                                popperClass="el-tooltip__standard"
                                placement="left"
                            >
                                <span class="pa-absolute pa-left-0 pa-top-0 pa-h-full pa-w-[3px] pa-bg-green-500" />
                            </ElTooltip>
                            <span
                                v-if="!item.READ_STATUS"
                                class="pa-absolute pa-left-2 pa-top-[18px] pa-h-1 pa-w-1 pa-rounded-[50%] pa-bg-blue-500"
                            />
                            <div class="pa-flex pa-h-6 pa-items-center">
                                <div class="pa-flex pa-h-full pa-flex-1 pa-items-center">
                                    <span class="pa-mr-1 pa-text-grey-90">{{ index + 1 }}</span>
                                    <Legal
                                        class="family-patents-list__legal"
                                        :isSimple="true"
                                        :legalStatus="[item.SIMPLE_LEGAL_STATUS]"
                                        :modeType="'dot'"
                                    />
                                    <span
                                        class="pa-text-green-600"
                                        v-html="item.PN"
                                    />
                                    <Legal
                                        class="family-patents-list__legal pa-flex pa-items-center"
                                        :legalObject="item.legalInfo"
                                        :clickCallBack="onClickLegal"
                                        :callbackParams="{ patentId: item.patent_id }"
                                    />

                                    <ElTooltip
                                        v-if="isFamilySearchFromWorkspace"
                                        :visibleArrow="false"
                                        :disabled="item.isSaved"
                                        :content="saveToCurrentWs ? t('patent-view.familySearch.saveToWs') : t('patent-view.familySearch.noPermissionToSave')"
                                        :effect="saveToCurrentWs ? 'dark' : 'light'"
                                        popperClass="el-tooltip__standard"
                                        placement="top"
                                    >
                                        <div
                                            class="save-to-workspace__content pa-ml-1"
                                            :class="{
                                                'save-to-workspace__content--save': item.isSaved
                                            }"
                                        >
                                            <PtIcon
                                                v-if="!item.isSaved"
                                                icon="SolidFolderClosed2"
                                                class="!pa-text-[20px]"
                                                :class="{
                                                    '!pa-text-gray-55': !saveToCurrentWs,
                                                    '!pa-text-gray-90 hover:!pa-text-gray-450': saveToCurrentWs
                                                }"
                                                @click.stop="saveToWorkspace(item)"
                                            />
                                            <PtIcon
                                                v-else
                                                icon="SolidTick"
                                                class="save-to-workspace__content__saved-icon pa-ml-1 !pa-text-[20px] !pa-text-green-500"
                                            />
                                        </div>
                                    </ElTooltip>
                                </div>
                                <MultiMonitorToggle
                                    :patentId="item.patent_id"
                                    :isInPatentList="true"
                                    class="family-patents-list__multi-monitor group-hover:pa-visible"
                                    @click.native="switchDelay(item.patent_id)"
                                />
                            </div>
                            <div
                                v-if="item.frequency"
                                class="pa-mt-1 pa-flex"
                            >
                                <ElTooltip
                                    v-for="(count, key) in item.frequency"
                                    :key="key"
                                    :visibleArrow="false"
                                    :content="t(`patent-view.familySearch.keywordsHit.${key}`)"
                                    placement="bottom"
                                    effect="dark"
                                    popperClass="el-tooltip__standard"
                                >
                                    <span
                                        class="pa-mr-2 pa-rounded pa-px-2 pa-py-0.5 hover:pa-underline"
                                        :class="{
                                            'pa-bg-green-200 pa-text-green-600': count > 0,
                                            'pa-bg-gray-30 group-[.list-active]:pa-bg-gray-40': count === 0,
                                        }"
                                        @click.stop="goToHitDetails(item.patent_id, key)"
                                    >
                                        <span
                                            :class="{
                                                'pa-text-gray-90': count === 0,
                                            }"
                                        >{{ key }}</span>
                                        <span>{{ formatNumber(count) }}</span>
                                    </span>
                                </ElTooltip>
                            </div>
                        </div>
                    </div>
                </div>
                <!--next-->
                <div
                    v-if="hasNextPage"
                    class="pa-relative"
                >
                    <FamilyPatentsLoading />
                    <PtLazy
                        v-if="!nextPageLoading"
                        :key="`lazy-${_page}`"
                        :options="{ delay: 200 }"
                        :lazyTask="getPatentsListNextPage"
                        class="pa-absolute pa-left-0 pa-top-0 pa-h-full pa-w-full"
                    />
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import _ from 'lodash';
import { ref, defineExpose, computed, watch, defineEmits } from 'vue';
import { useRoute } from 'vue-router/composables';
import { useRouter } from 'vue-router/composables';
import { getFamilyPatentListApi } from 'library-@/apis/core_basic_api/family';
import { getWorkspaceInfoApi, savePatentsToWorkspaceApi } from 'library-@/apis/workspace_api/view';
import Legal from '@/components/legal/legal.vue';
import MultiMonitorToggle from '@/components/multi-monitors/MultiMonitorToggle';
import FamilyPatentsLoading from './FamilyPatentsLoading.vue';
import formatNumber from '@/lib/familytree/utils/formatNumber';
import { Tooltip, Popover } from 'element-ui';
import { Lazy as PtLazy, Icon as PtIcon, Toast } from 'patsnap-biz';
import { useFamilySearch } from '@/composables/use-family-search';
import { useFamilySearchPage } from '@/composables/use-family-search-page';
import { useFamilySearchPatentInfo } from '@/composables/use-family-search-patent-info';
import { useFamilySearchUrlParams } from '@/composables/use-family-search-url-params';
import { useI18n } from '@/composables/use-i18n';
import abstManifest from '@/subview_plugins/Abst/manifest.js';
import clmsManifest from '@/subview_plugins/Clms/manifest.js';
import descManifest from '@/subview_plugins/Desc/manifest.js';
import litigationManifest from '@/subview_plugins/Litigation/manifest';
import licensingManifest from '@/subview_plugins/Licensing/manifest';
import familySearchOverviewMf from '@/subview_plugins/FamilySearchOverview/manifest.js';
import { icons } from 'library-@/utils/workspaceIcon';
import type {
    IApiFamilyPatentsRequestBody,
    IApiFamilyPatentsResponseBody,
    IFamilyPatentItem,
    IFormatFamilyPatentItem,
} from '../../types/familyPatentsList';
import '@patsnap-ui/icon/assets/solid/FolderClosed2.svg';
import '@patsnap-ui/icon/assets/solid/tick.svg';

const ElTooltip = Tooltip;
const ElPopover = Popover;

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { isInFamilyOverview, isFamilySearchFromWorkspace, isApnoSearchType } = useFamilySearch();

const {
    patentId,
    familySearchType,
    q,
    efq,
    page,
    type,
    folderId,
    workspaceId,
} = useFamilySearchUrlParams();

const {
    deferred: familySearchPageDeferred,
    index,
    familyKeyWords,
    currentActiveItem,
} = useFamilySearchPage();

const { deferred: familySearchPatentInfoDeferred, patentInfo } = useFamilySearchPatentInfo();


const _page = ref(page.value);
const hitSize = ref<number | undefined>(undefined);
const noHitSize = ref<number | undefined>(undefined);

const patentData = ref(true); // 右侧列表为true
const skipNoHit = ref(false); // 是否忽略未命中专利

const loadingList = ref(false);
const patentsList = ref<IFormatFamilyPatentItem[]>([]);

const hasNextPage = ref(false);
const nextPageLoading = ref(false);

const isFirst = ref(false);
const isLast = ref(false);

const resetState = () => {
    _page.value = page.value;
    hitSize.value = undefined;
    noHitSize.value = undefined;
    hasNextPage.value = false;
};

const getPatentsList:() => Promise<IFormatFamilyPatentItem[]> = async () => {
    // 这里用到的patent_id是同族代表的id
    const { family_id, patent_id, pn } = currentActiveItem.value;
    // workspace 同申请跳转过来时，family_id为空
    if (/*!family_id || */!patent_id) return;

    const { patsnap_family_count } = patentInfo.value;

    const params:IApiFamilyPatentsRequestBody = {
        q: q.value,
        efq: efq.value,
        page: _page.value,
        type: type.value,
        family_type: familySearchType.value,
        family_keywords: familyKeyWords.value,
        family_id,
        patent_id,
        pn,
        family_count: patsnap_family_count,
        hit_size: hitSize.value,
        no_hit_size: noHitSize.value,
        patent_data: patentData.value,
        skip_no_hit: skipNoHit.value,
        folder_id: folderId.value,
    };

    return await getFamilyPatentListApi(params).then((res:IApiFamilyPatentsResponseBody) => {
        // 每次调完接口都要更新一下hitSize和noHitSize
        hitSize.value = _.get(res, 'hit_size', 0);
        noHitSize.value = _.get(res, 'no_hit_size', 0);
        hasNextPage.value = _.get(res, 'has_next', false);
        const dataItems = _.get(res, 'data_items', []);
        return _.map(dataItems, (item:IFamilyPatentItem) => {
            const info:IFormatFamilyPatentItem = {
                ..._.pick(item, ['READ_STATUS', 'SIMPLE_LEGAL_STATUS', 'PN', 'patent_id', 'hit', 'saved_in_folder']),
                legalInfo: _.pick(item, ['HAS_LICENSE', 'HAS_LITIGATION', 'HAS_STANDARD']),
                frequency: isFamilySearchFromWorkspace.value ? undefined : {
                    'T/A': _.get(item, 'FREQUENCY_OVERVIEW.ABST', 0) + _.get(item, 'FREQUENCY_OVERVIEW.TTL', 0),
                    C: _.get(item, 'FREQUENCY_OVERVIEW.CLMS', 0),
                    D: _.get(item, 'FREQUENCY_OVERVIEW.DESC', 0),
                },
                // workspace用于判断是否保存到空间
                isSaved: !!item.saved_in_folder,
            };
            return info;
        });
    });
};

/*
* 分页相关
* */
const getPatentsListNextPage = async () => {
    nextPageLoading.value = true;
    _page.value += 1;
    const res = await getPatentsList();
    patentsList.value = _.concat(patentsList.value, res);
    nextPageLoading.value = false;
};

/*
* 路由重复避免跳转
* */
const onBeforeRouterChange = (routerParams) => {
    if (
        _.isEqual(routerParams.query, route.query)
        && (routerParams.path && routerParams.path === route.path)
    ) {
        console.log('相同路由，不跳转');
        return false;
    }
    router.push({
        ...routerParams,
        params: route.params,
    });
};

/*
* 隐藏/显示未命中的专利
* */
const changePatentsListForHit = async () => {
    resetState();
    loadingList.value = true;
    skipNoHit.value = !skipNoHit.value;
    patentsList.value = await getPatentsList();
    // 获取第一条专利的id并跳转
    const firstPatentId = _.get(patentsList.value, '[0].patent_id', '');
    if (firstPatentId) {
        const query = {
            ...route.query,
            patentId: firstPatentId,
        };
        onBeforeRouterChange({
            query,
            path: abstManifest.exclusiveRoutePath,
        });
    }
    loadingList.value = false;
};

/*
* route link链接
* */

const hasFamilyOverview = computed(() => {
    return !isApnoSearchType.value;
});

const goToFamilyOverview = () => {
    const query = {
        ...route.query,
        patentId: currentActiveItem?.value?.patent_id,
    };
    onBeforeRouterChange({
        query,
        path: familySearchOverviewMf.exclusiveRoutePath,
    });
};

const goToPatentDetail = (data) => {
    const query = {
        ...route.query,
        patentId: data.patent_id,
    };

    const routerParams = isInFamilyOverview.value ? {
        path: abstManifest.exclusiveRoutePath,
        query,
    } : {
        query,
    };
    onBeforeRouterChange(routerParams);
    window.rvTrack('Toggle Family View Patent List');
};

/*
* 暴漏给外部使用，用于快捷键切换专利
* */
const switchDelay = (patentId:string, direction:number = 0) => {
    const currentIndex = _.findIndex(patentsList.value, { patent_id: patentId });
    const lastIndex = patentsList.value.length - 1;
    const targetIndex = currentIndex + direction;
    const firstItem = _.head(patentsList.value);
    const firstPatentId = firstItem?.patent_id;

    // overview快捷键切换专利时
    if (isInFamilyOverview.value && firstItem) {
        onBeforeRouterChange({
            query: {
                ...route.query,
                patentId: firstPatentId,
            },
            path: abstManifest.exclusiveRoutePath,
        });
        return;
    }

    if (firstPatentId === patentId && targetIndex < 0) {
        goToFamilyOverview();
        return;
    }

    if (targetIndex > lastIndex || targetIndex < 0) {
        return;
    }

    const newPatentId = patentsList.value[targetIndex]?.patent_id;

    onBeforeRouterChange({
        query: {
            ...route.query,
            patentId: newPatentId,
        },
    });
};
defineExpose({ switchDelay });

const emit = defineEmits(['pageInfoUpdate']);

/*
* 更新已读未读状态
* */
// 唯一ID 带上是否在overview的状态，来区分overview页面还是族代表专利的页面
const familyContId = computed(() => {
    return `${patentId.value}__${isInFamilyOverview.value}`;
});

const readMe = () => {
    const index = _.findIndex(patentsList.value, { patent_id: patentId.value });
    if (index === -1) return;

    if (!hasFamilyOverview.value) {
        isFirst.value = index === 0;
    }
    const pnInfo = patentsList.value[index];
    pnInfo.READ_STATUS = true;

    isLast.value = (!hasNextPage.value && index >= patentsList.value.length - 1);
};

watch(() => isFirst.value, () =>{
    emit('pageInfoUpdate', { type: 'isFirst', value: isFirst.value });
});

watch(() => isLast.value, () =>{
    emit('pageInfoUpdate', { type: 'isLast', value: isLast.value });
});

watch(() => familyContId.value, () => {
    isFirst.value = isInFamilyOverview.value;
    if (!isInFamilyOverview.value) {
        readMe();
    }
},{ immediate: true });

/*
* 监听index变动 触发接口调用
* */
const workspaceInfo = ref({});
const saveToCurrentWs = ref(false);
watch(() => index.value, async () => {
    loadingList.value = true;

    await Promise.all([
        familySearchPatentInfoDeferred.value.promise,
        familySearchPageDeferred.value.promise,
    ]);

    if (isFamilySearchFromWorkspace.value) {
        const res = await getWorkspaceInfoApi({
            folder_id: folderId.value,
        });
        workspaceInfo.value = {
            spaceIcon: res?.space_icon,
            spaceS3Key: res?.space_s3_key,
            spaceName: res?.space_name,
            folderName: res?.current_folder?.folder_name,
        };
        saveToCurrentWs.value = res?.edit_patent || false;
    }

    resetState();

    patentsList.value = await getPatentsList();

    // 如果列表里没有当前URL上的patentId，则默认跳转到第一条
    const currentPatentInfo = _.find(patentsList.value, (item) => item.patent_id === patentId.value);
    const firstPatentId = _.get(patentsList.value, '[0].patent_id', '');
    if (!currentPatentInfo && firstPatentId) {
        router.push({
            query: {
                ...route.query,
                patentId: firstPatentId,
            },
            params: route.params,
        });
    }

    loadingList.value = false;

    readMe();
}, { immediate: true });

/*
* 跳转至命中专利
* */
const hitToDetailsPathMap = {
    'T/A': abstManifest.exclusiveRoutePath,
    'C': clmsManifest.exclusiveRoutePath,
    'D': descManifest.exclusiveRoutePath,
};

const goToHitDetails = (patentId:string, key:string) => {
    onBeforeRouterChange({
        query: {
            ...route.query,
            patentId,
        },
        path: hitToDetailsPathMap[key],
    });
};

/*
* 单条专利保存到工作空间
* */
const saveToWorkspace = (item:IFormatFamilyPatentItem) => {
    if (!saveToCurrentWs.value || item.isSaved) return;
    item.isSaved = true;
    savePatentsToWorkspaceApi({
        space_id: workspaceId.value,
        folder_id: folderId.value,
        auto: false,
        source_type: 'patentView',
        patent_count: 1,
        patent_ids: [item.patent_id],
    }).then(() => {
        Toast({
            message: t('patent-view.familySearch.saved'),
            showClose: true,
            type: 'success',
        });
    }).catch(err => {
        item.isSaved = false;
        Toast({
            message: err._error_msg || t('commonStatusCode.400'),
            showClose: true,
            type: 'error',
        });
    });
    window.rvTrack('Save2Workspace In Family View Patent List');
};

/*
* 点击诉讼之类的小icon
* */
const legalMapsPath = {
    'LITIGATION': litigationManifest.exclusiveRoutePath,
    'LICENSE': licensingManifest.exclusiveRoutePath,
    'STANDARD': abstManifest.exclusiveRoutePath,
};
const onClickLegal = (target, params, event) => {
    event?.preventDefault();
    const targetPatentId = _.get(params, 'patentId');
    onBeforeRouterChange({
        query: {
            ...route.query,
            patentId: targetPatentId,
        },
        path: legalMapsPath[target],
    });
};

</script>

<style lang="scss">
.family-patents-list {
    .family-patents-list__legal {
        @apply pa-flex pa-items-center;
        .legal-tag {
            @apply pa-mr-1;
        }
    }

    .family-patents-list__multi-monitor {
        @apply pa-mr-1 pa-invisible;

        &.multi-monitor-toggle--active {
            @apply pa-visible;
        }
    }

    .family-patents-list__compare {
        @apply pa-flex-shrink-0 pa-invisible;
        .compare-with-tag__tag {
            @apply pa-bg-gray-40 hover:pa-bg-[rgba(52,69,99,0.2)];
            @apply pa-fill-gray-450 #{!important};
        }
    }

    .save-to-workspace__content--save {
        .save-to-workspace__content__saved-icon {
            display: none;
            animation: tick-icon 1.5s ease-in-out;
        }
    }

    @keyframes tick-icon {
        0% {
            display: block;
            opacity: 0;

        }
        20% {
            display: block;
            opacity: 1;

        }
        70% {
            display: block;
            opacity: 1;
        }
        100% {
            display: none;
            opacity: 0;
        }
    }

}
</style>
